import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, X, Compass } from "@phosphor-icons/react";
import SessionView from "@/pages/SessionView";
import NewChallenge from "@/pages/NewChallenge";
import api, { DEMO_ID } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import cgiLogo from "@/assets/cgi-logo.png";

const STEPS = [
  { view: "entry", target: null, title: "Welcome to a sample session",
    body: "This is a real example run through the Executive Strategy Room: an enterprise-software CEO losing top customers to a faster competitor. We'll start where every challenge starts, then walk through the three steps the app takes." },
  { view: "entry", target: "narrative-box", title: "Describe the challenge",
    body: "You begin by writing what's happening in your own words. It doesn't need to be organised. Tap the microphone to speak instead of typing. This is what our sample CEO wrote." },
  { view: "entry", target: "optional-fields-box", title: "Add supporting detail (optional)",
    body: "If you can, say why it matters now, what you want, what's been tried, what would be unacceptable, and what evidence you have. Each answer sharpens the analysis." },
  { view: "entry", target: "examine-row", title: "Examine the challenge",
    body: "Click Examine and the app reads everything, which takes a minute or two. Click it here, or press Next, to see what it produced for this sample." },
  { view: "map", target: "summary-problem", title: "Step 1 — The Problem",
    body: "The app restates your challenge faithfully, separating facts from assumptions, so you can check it understood you before going further." },
  { view: "map", target: "summary-aim", title: "What you're trying to achieve",
    body: "It separates the problem from the goal behind it, so the solution aims at what you actually want, not just the symptom." },
  { view: "map", target: "summary-clarity", title: "Where more clarity is needed",
    body: "The app asks pointed questions about anything important that's missing. Answer them and the analysis updates. Answered questions don't come back." },
  { view: "map", target: "problem-console-open", title: "Steer the analysis anytime",
    body: "Disagree or have new information? Type or speak a correction here, or attach a document, and the picture is rebuilt around it." },
  { view: "areas", target: "areas-diagram", title: "Step 2 — The Problem Space",
    body: "A hard challenge is never one problem. Here it's broken into categories of interlocking problems. Click any circle to see what it holds and why it matters." },
  { view: "areas", target: "explore-relations-heading", title: "Tensions between problems",
    body: "Fixing one problem can worsen another. These are the trade-offs a real solution has to handle, explained in plain language." },
  { view: "areas", target: "command-open", title: "Ask questions or reshape it",
    body: "Ask why something is there, or tell the app to add, merge, or reprioritise problems. It replies and updates the map." },
  { view: "solutions", target: "problem-beat", title: "Step 3 — Strategies",
    body: "The app composes one systemic solution: a coordinated set of moves that act on several problems at once, not a list of separate fixes." },
  { view: "solutions", target: "systems-map", title: "The systems map",
    body: "Inner boxes are your problems; outer boxes are the solutions. Lines show how they affect each other. Click any box or line in this sample to see an explanation." },
  { view: "solutions", target: "causal-story", title: "The strategies, in order",
    body: "Each move is explained: what it does, which problems it helps, and what it makes possible next. You can view an action plan or print a report." },
  { view: "solutions", target: "revise-box", title: "Not right? Rebuild it",
    body: "Describe what feels wrong and the app re-architects the solution. You stay in charge at every step." },
  { view: "solutions", target: null, title: "Ready to try your own challenge?",
    body: "Everything you've seen was generated from a few paragraphs about one company's problem. Create a free account and describe yours.", final: true },
];

const useHighlight = (target, view) => {
  useEffect(() => {
    if (!target) return;
    let el; let tries = 0;
    const t = setInterval(() => {
      el = document.querySelector(`[data-testid="${target}"]`);
      if (el || ++tries > 30) {
        clearInterval(t);
        if (el) { el.classList.add("tour-highlight"); el.scrollIntoView({ behavior: "smooth", block: "center" }); }
      }
    }, 150);
    return () => { clearInterval(t); el?.classList.remove("tour-highlight"); };
  }, [target, view]);
};

const TourCard = ({ view, setView, hidden, setHidden, i, setI }) => {
  const step = STEPS[i];
  useEffect(() => { if (setView && step.view !== view) setView(step.view); }, [i]); // eslint-disable-line react-hooks/exhaustive-deps
  useHighlight(hidden ? null : step.target, view);

  if (hidden) {
    return (
      <button data-testid="tour-reopen-btn" onClick={() => setHidden(false)}
        className="fixed bottom-16 right-6 z-50 inline-flex items-center gap-2 rounded-full border border-primary bg-primary px-4 py-2 text-xs font-medium text-primary-foreground shadow-lg hover:bg-primary/85">
        <Compass size={14} weight="bold" /> Resume tour
      </button>
    );
  }
  return (
    <div data-testid="tour-card" className="reveal fixed inset-x-3 bottom-14 z-50 rounded-lg sm:inset-x-auto sm:bottom-auto sm:right-6 sm:top-36 sm:w-[340px] rounded-lg border border-primary/40 bg-card p-5 shadow-2xl">
      <div className="mb-2 flex items-center justify-between">
        <span data-testid="tour-progress" className="font-mono text-[11px] text-muted-foreground">{i + 1} / {STEPS.length}</span>
        <button data-testid="tour-hide-btn" onClick={() => setHidden(true)} title="Hide tips and explore freely" className="text-muted-foreground hover:text-foreground"><X size={16} /></button>
      </div>
      <h3 data-testid="tour-title" className="font-serif text-xl leading-tight">{step.title}</h3>
      <p data-testid="tour-body" className="mt-2 text-sm leading-relaxed text-foreground/85">{step.body}</p>
      <div className="mt-4 h-1 w-full rounded-full bg-muted"><div className="h-1 rounded-full bg-primary transition-[width] duration-300" style={{ width: `${((i + 1) / STEPS.length) * 100}%` }} /></div>
      <div className="mt-4 flex items-center justify-between gap-2">
        <button data-testid="tour-back-btn" disabled={i === 0} onClick={() => setI(i - 1)}
          className="inline-flex items-center gap-1.5 rounded-sm border border-border px-3 py-2 text-xs font-medium hover:bg-muted disabled:opacity-40"><ArrowLeft size={13} /> Back</button>
        {step.final ? (
          <Link to="/login?mode=register" data-testid="tour-signup-btn" className="inline-flex items-center gap-1.5 rounded-sm border border-primary bg-primary px-4 py-2 text-xs font-medium text-primary-foreground hover:bg-primary/85">Start my own challenge <ArrowRight size={13} weight="bold" /></Link>
        ) : (
          <button data-testid="tour-next-btn" onClick={() => setI(i + 1)}
            className="inline-flex items-center gap-1.5 rounded-sm border border-primary bg-primary px-4 py-2 text-xs font-medium text-primary-foreground hover:bg-primary/85">Next <ArrowRight size={13} weight="bold" /></button>
        )}
      </div>
    </div>
  );
};

const DemoBar = () => {
  const { user } = useAuth();
  return (
    <header className="sticky top-0 z-40 h-16 border-b border-border bg-background/90 backdrop-blur-md">
      <div className="flex h-full w-full items-center justify-between gap-4 px-8">
        <div className="flex min-w-0 items-center gap-4">
          <a href="https://www.commongroundinstitute.net" target="_blank" rel="noopener noreferrer" className="shrink-0"><img src={cgiLogo} width="1200" height="415" alt="Common Ground Institute" className="h-8 w-auto" /></a>
          <Link to="/" data-testid="demo-home-link" className="truncate font-serif text-xl tracking-tight">Executive Strategy Room</Link>
          <span data-testid="demo-badge" className="hidden shrink-0 rounded-full border border-primary/40 bg-primary/10 px-2.5 py-0.5 text-[11px] font-medium uppercase tracking-wide text-primary sm:inline">Sample walkthrough</span>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <Link to="/" data-testid="demo-exit-btn" className="rounded-sm border border-border px-3 py-2 text-xs font-medium hover:bg-muted">Exit tour</Link>
          {!user && <Link to="/login?mode=register" data-testid="demo-getstarted-btn" className="rounded-sm border border-primary bg-primary px-4 py-2 text-xs font-medium text-primary-foreground hover:bg-primary/85">Get started</Link>}
        </div>
      </div>
    </header>
  );
};

export default function DemoWalkthrough() {
  const [hidden, setHidden] = useState(false);
  const [i, setI] = useState(0);
  const [sample, setSample] = useState(null);
  useEffect(() => { api.getSession(DEMO_ID).then((d) => setSample(d.session.input)).catch(() => {}); }, []);
  const tour = { hidden, setHidden, i, setI };
  const onEntry = STEPS[i].view === "entry";
  return (
    <div data-testid="demo-walkthrough" className={hidden ? "" : "xl:pr-[372px]"}>
      <DemoBar />
      {onEntry ? (
        <>
          {sample && <NewChallenge sample={sample} onSampleSubmit={() => setI(STEPS.findIndex((s) => s.view !== "entry"))} />}
          <TourCard view="entry" {...tour} />
        </>
      ) : (
        <SessionView demoId={DEMO_ID} renderTour={(p) => <TourCard {...p} {...tour} />} />
      )}
    </div>
  );
}
